import {History} from "@mui/icons-material";
import {Card, CardContent, CardHeader, Grid} from "@mui/material";
import {TemporalFormat, tryParseInstant} from "@variocube/app-ui";
import React, {Fragment} from "react";
import {Invoice, InvoiceState} from "../../../api";
import {Typography} from "../../../component/typography";
import {useLocalization} from "../../../i18n";
import {notBlank} from "../../../util";

export function InvoiceHistory({invoice}: { invoice: Invoice }) {
	const {t, e} = useLocalization();

	const history = getHistory(invoice, t);

	return (
		<Card>
			<CardHeader title={<Typography startAdornment={<History />}>{t("invoices.details.history")}</Typography>} />
			<CardContent>
				<Grid container spacing={1}>
					{history?.map(item => (
						<Fragment key={item.key}>
							<Grid item xs={6}>
								{item.key === RECIPIENT_CORRECTED
									? t("invoices.details.recipientCorrection.title")
									: e("invoices.invoiceStates", item.key)}
							</Grid>
							<Grid item xs={6} textAlign="right">
								<TemporalFormat value={tryParseInstant(item.date)} />
							</Grid>
							{notBlank(item.message) && (
								<Grid item xs={12}>
									<Typography>{item.message}</Typography>
								</Grid>
							)}
						</Fragment>
					))}
				</Grid>
			</CardContent>
		</Card>
	);
}

// Not an InvoiceState: the scan service (Hypatos) transferred the invoice under a different
// recipient company than the one it was routed to. Inbox/outbox stay unchanged.
const RECIPIENT_CORRECTED = "RECIPIENT_CORRECTED";

interface HistoryItem {
	key: InvoiceState | typeof RECIPIENT_CORRECTED;
	date?: string;
	message?: string;
}

type Translate = ReturnType<typeof useLocalization>["t"];

function getHistory(invoice: Invoice, t: Translate) {
	const correction = invoice.recipientCorrection;
	const history: HistoryItem[] = [
		{key: "SUBMITTED", date: invoice.submitted},
		{key: "CHECKED", date: invoice.checked},
		{key: "AUDITED", date: invoice.audited},
		{key: "OEBB_REJECTED", date: invoice.oebbRejected},
		{key: "ROUTED", date: invoice.routed},
		{key: "UPLOADED", date: invoice.uploaded},
		{key: "OCR_SUCCESSFUL", date: invoice.ocrSuccessful},
		{key: "OCR_FAILED", date: invoice.ocrFailed, message: invoice.ocrErrorMsg},
		{
			key: RECIPIENT_CORRECTED,
			date: correction?.corrected,
			message: correction && t("invoices.details.recipientCorrection.message", {
				originalBca: correction.originalBca ?? "",
				bca: correction.bca ?? "",
				companyCode: correction.companyCode ?? "",
				companyName: correction.companyName ?? "",
			}),
		},
		{key: "PROCESSED", date: invoice.processed},
		{key: "PAYED_OUT", date: invoice.payedOut},
		{key: "ARCHIVED", date: invoice.filesDeleted},
	];

	return history.filter(h => Boolean(h.date))
		.sort((a, b) => a.date?.localeCompare(b.date ?? "") ?? 1);
}
